import {FC, ReactNode, useCallback, useState, useEffect, useRef} from 'react'
import {SelectProvider, useComboboxStore} from '@ariakit/react'
import {
    SelectComboboxInput,
    SelectItem,
    SelectPopover,
    SelectTrigger
} from '@components/ui/select-atoms/SelectAtoms.tsx'
import {Checkbox} from '@components/commons/checkbox/ChekBox.tsx'
import {
    StyledDisplayValueWrapper,
    StyledSelectBaseListWrapper,
    StyledSelectNoResult,
    StyledCountIndicator
} from '@components/ui/multi-select/style.ts'
import {useTranslation} from 'react-i18next'
import {debounce} from '@utilities/helpers.ts'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {Spinner} from '@components/ui/spinner/Spinner.tsx'

export interface OptionType {
    id: string
    name: string
}

export interface MultiSelectBaseProps {
    options: OptionType[]
    displayLabel: string
    displayIcon: ReactNode
    searchPlaceholder: string
    value: OptionType[]
    onChange: (value: OptionType[]) => void
    sameWidth?: boolean
    isLoading?: boolean
}

export const MultiSelectBase: FC<MultiSelectBaseProps> = ({
    value,
    onChange,
    options,
    displayLabel,
    displayIcon,
    searchPlaceholder,
    sameWidth = false,
    isLoading = false
}) => {
    const {t} = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [filteredOptions, setFilteredOptions] = useState<OptionType[]>(options)
    const inputRef = useRef<HTMLInputElement | null>(null)
    const comboboxStore = useComboboxStore()

    const debouncedSearch = useCallback(
        debounce((query: string) => {
            if (!options) return
            setFilteredOptions(options.filter(option => option.name.toLowerCase().includes(query.toLowerCase())))
        }, 500),
        [options]
    )

    useEffect(() => {
        debouncedSearch(searchQuery)
    }, [searchQuery, debouncedSearch])

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                comboboxStore.setOpen(true)
                inputRef.current?.focus()
            }, 50)
        }
    }, [isOpen, comboboxStore])

    const handleClose = () => {
        setIsOpen(false)
        setSearchQuery('')
        setFilteredOptions(options)
    }

    const isSelected = (id: string) => value.some(v => v.id === id)

    const handleChange = (item: OptionType) => {
        const exists = value.some(v => v.id === item.id)
        const newValue = exists ? value.filter(v => v.id !== item.id) : [...value, item]

        onChange(newValue)
    }

    return (
        <SelectProvider value={value.map(v => v.id)}>
            <SelectTrigger
                displayValue={() => (
                    <StyledDisplayValueWrapper gap={2} align="center">
                        <p>
                            {displayLabel}
                            <StyledCountIndicator className={value.length > 0 ? 'visible' : ''}>
                                {value.length > 0 && ` (${value.length})`}
                            </StyledCountIndicator>
                        </p>
                        {displayIcon}
                    </StyledDisplayValueWrapper>
                )}
                onClick={() => setIsOpen(prev => !prev)}
            />
            <SelectPopover gutter={8} sameWidth={sameWidth} unmountOnHide onClose={handleClose}>
                <SelectComboboxInput
                    ref={inputRef}
                    store={comboboxStore}
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    autoFocus
                    tabIndex={-1}
                    onChange={event => setSearchQuery(event.target.value)}
                />
                <StyledSelectBaseListWrapper>
                    {isLoading ? (
                        <Flexbox justify="center" align="center" gap={4} className="spinner-container">
                            <Spinner />
                        </Flexbox>
                    ) : !filteredOptions?.length ? (
                        <StyledSelectNoResult>{t('commons:no_results_found_for')}</StyledSelectNoResult>
                    ) : (
                        filteredOptions?.map(option => (
                            <SelectItem
                                key={option.id}
                                value={option.id}
                                onClick={() => handleChange(option)}
                                setValueOnClick
                            >
                                <Checkbox
                                    id={option.id}
                                    checked={isSelected(option.id)}
                                    onChange={() => handleChange(option)}
                                />
                                <span>{option.name}</span>
                            </SelectItem>
                        ))
                    )}
                </StyledSelectBaseListWrapper>
            </SelectPopover>
        </SelectProvider>
    )
}
